import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { EarningsData, ConsolidatedEarningsResponse } from '../types';
import ErrorHandler from './ErrorHandler';

interface EarningsComponentProps {
  className?: string;
}

export const EarningsComponent: React.FC<EarningsComponentProps> = ({ className = '' }) => {
  const [sessionType, setSessionType] = useState<'premarket' | 'afterhours' | 'both'>('both');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [data, setData] = useState<ConsolidatedEarningsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  // Set default to show next 3 days including today
  useEffect(() => {
    const today = new Date();
    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0];
    };
    // Set to today's date for the default view
    setSelectedDate(formatDate(today));
  }, []);

  // Fetch earnings data based on filters
  const fetchEarningsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user wants specific date filter or default range
      const isDefaultView = selectedDate === new Date().toISOString().split('T')[0];
      
      if (isDefaultView) {
        // Default view: Show next 3 days including today
        console.log('Fetching earnings data for next 3 days (including today)...');
        const today = new Date();
        const allEarningsData: EarningsData[] = [];
        let totalPremarketCount = 0;
        let totalAfterhoursCount = 0;
        
        // Make API calls for next 3 days including today
        for (let i = 0; i < 3; i++) {
          const targetDate = new Date(today);
          targetDate.setDate(today.getDate() + i);
          const dateString = targetDate.toISOString().split('T')[0];
          
          try {
            console.log(`Fetching earnings for date: ${dateString}`);
            const response = await apiService.getConsolidatedEarnings({
              session_type: sessionType,
              date: dateString
            });
            
            if (response.data && response.data.length > 0) {
              console.log(`Found ${response.data.length} earnings for ${dateString}`);
              allEarningsData.push(...response.data);
              totalPremarketCount += response.premarket_count || 0;
              totalAfterhoursCount += response.afterhours_count || 0;
            }
          } catch (err) {
            console.warn(`Failed to fetch earnings for ${dateString}:`, err);
            // Continue with other dates even if one fails
          }
        }
        
        // Consolidate all results
        const consolidatedResponse: ConsolidatedEarningsResponse = {
          data: allEarningsData,
          premarket_count: totalPremarketCount,
          afterhours_count: totalAfterhoursCount,
          total_count: allEarningsData.length,
          success: true,
          message: `Retrieved earnings data for next 3 days`,
          requested_date: `${today.toISOString().split('T')[0]} to ${new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}`
        };
        
        console.log('Consolidated earnings response:', consolidatedResponse);
        setData(consolidatedResponse);
      } else {
        // User has selected a specific date - use that as filter
        const params: any = {
          session_type: sessionType,
          date: selectedDate
        };
        
        console.log('Fetching earnings data with filters:', params);
        const response = await apiService.getConsolidatedEarnings(params);
        console.log('Earnings API response:', response);
        setData(response);
      }
      
    } catch (err) {
      console.error('Error fetching earnings data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when dependencies change
  useEffect(() => {
    if (selectedDate) {
      fetchEarningsData();
    }
  }, [sessionType, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  const formatEps = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return `$${value.toFixed(2)}`;
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getEpsColor = (actual: number | null | undefined, estimate: number | null | undefined): string => {
    if (actual === null || actual === undefined || estimate === null || estimate === undefined) {
      return 'var(--text-primary)';
    }
    if (actual > estimate) return 'var(--accent-green)';
    if (actual < estimate) return 'var(--accent-red)';
    return 'var(--text-primary)';
  };

  const getRevenueColor = (actual: number | null | undefined, estimate: number | null | undefined): string => {
    if (actual === null || actual === undefined || estimate === null || estimate === undefined) {
      return 'var(--text-primary)';
    }
    if (actual > estimate) return 'var(--accent-green)';
    if (actual < estimate) return 'var(--accent-red)';
    return 'var(--text-primary)';
  };

  const handleRefresh = () => {
    fetchEarningsData();
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  const clearFilters = () => {
    setSessionType('both');
    // Reset to today's date
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
  };

  return (
    <div className={`earnings-component ${className}`}>
      <div className="component-header">
        <h3 className="component-title">
          📊 Earnings Calendar
        </h3>
        <div className="header-actions">
          <button 
            onClick={clearFilters}
            className="clear-filters-button"
            disabled={loading}
          >
            Clear Filters
          </button>
          <button 
            onClick={handleRefresh}
            className="refresh-button"
            disabled={loading}
          >
            {loading ? '🔄' : '↻'} Refresh
          </button>
        </div>
      </div>

      <div className="earnings-filters">
        <div className="filter-group">
          <label htmlFor="session-type">Session:</label>
          <select
            id="session-type"
            value={sessionType}
            onChange={(e) => setSessionType(e.target.value as 'premarket' | 'afterhours' | 'both')}
            className="filter-select"
          >
            <option value="both">Both Sessions</option>
            <option value="premarket">Premarket</option>
            <option value="afterhours">After Hours</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="date-filter">Date:</label>
          <input
            id="date-filter"
            type="date"
            value={selectedDate}
            onChange={(e) => handleDateChange(e.target.value)}
            className="filter-input"
          />
        </div>
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span className="loading-text">Loading earnings data...</span>
        </div>
      )}

      {error && (
        <ErrorHandler 
          error={error} 
          onRetry={handleRefresh}
          title="Earnings Data Error"
        />
      )}

      {data && !loading && !error && (
        <div className="earnings-content">
          <div className="earnings-summary">
            <div className="summary-item">
              <span className="summary-label">TOTAL REPORTS:</span>
              <span className="summary-value">{data.total_count || 0}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">PREMARKET:</span>
              <span className="summary-value">{data.premarket_count || 0}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">AFTER HOURS:</span>
              <span className="summary-value">{data.afterhours_count || 0}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">DATE RANGE:</span>
              <span className="summary-value">
                {selectedDate === new Date().toISOString().split('T')[0] 
                  ? 'Next 3 Days' 
                  : formatDate(selectedDate)}
              </span>
            </div>
          </div>

          {data.data && data.data.length > 0 ? (
            <div className="earnings-grouped-view">
              {(() => {
                // Group earnings by date
                const groupedByDate = data.data.reduce((acc: any, earnings: EarningsData) => {
                  const dateKey = formatDate(earnings.report_date);
                  if (!acc[dateKey]) {
                    acc[dateKey] = [];
                  }
                  acc[dateKey].push(earnings);
                  return acc;
                }, {});

                // Sort dates
                const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
                  return new Date(a).getTime() - new Date(b).getTime();
                });

                return sortedDates.map((dateKey) => {
                  const dayEarnings = groupedByDate[dateKey];
                  const premarketEarnings = dayEarnings.filter((e: EarningsData) => e.earnings_type === 'premarket');
                  const afterhoursEarnings = dayEarnings.filter((e: EarningsData) => e.earnings_type === 'afterhours');

                  return (
                    <div key={dateKey} className="earnings-date-group">
                      <div className="date-header">
                        <h3 className="date-title">{dateKey}</h3>
                        <div className="date-summary">
                          <span className="date-count">{dayEarnings.length} {dayEarnings.length === 1 ? 'company' : 'companies'}</span>
                        </div>
                      </div>

                      <div className="sessions-container">
                        {/* Premarket Session */}
                        {premarketEarnings.length > 0 && (
                          <div className="session-group premarket-session">
                            <div className="session-header">
                              <span className="session-icon">🌅</span>
                              <h4 className="session-title">Premarket</h4>
                              <span className="session-count">{premarketEarnings.length}</span>
                            </div>
                            <div className="earnings-cards">
                              {premarketEarnings.map((earnings: EarningsData, index: number) => (
                                <div key={`${earnings.ticker}-${index}`} className="earnings-card">
                                  <div className="card-header">
                                    <span className="ticker-symbol">{earnings.ticker || '-'}</span>
                                  </div>
                                  <div className="company-name">{earnings.company_name || '-'}</div>
                                  <div className="card-metrics">
                                    <div className="metric-row">
                                      <span className="metric-label">EPS:</span>
                                      <span className="metric-value" style={{ color: getEpsColor(earnings.eps_actual, earnings.eps_estimate) }}>
                                        {formatEps(earnings.eps_actual)}
                                      </span>
                                      <span className="metric-estimate">est. {formatEps(earnings.eps_estimate)}</span>
                                    </div>
                                    <div className="metric-row">
                                      <span className="metric-label">Revenue:</span>
                                      <span className="metric-value" style={{ color: getRevenueColor(earnings.revenue_actual, earnings.revenue_estimate) }}>
                                        {formatCurrency(earnings.revenue_actual)}
                                      </span>
                                      <span className="metric-estimate">est. {formatCurrency(earnings.revenue_estimate)}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* After Hours Session */}
                        {afterhoursEarnings.length > 0 && (
                          <div className="session-group afterhours-session">
                            <div className="session-header">
                              <span className="session-icon">🌙</span>
                              <h4 className="session-title">After Hours</h4>
                              <span className="session-count">{afterhoursEarnings.length}</span>
                            </div>
                            <div className="earnings-cards">
                              {afterhoursEarnings.map((earnings: EarningsData, index: number) => (
                                <div key={`${earnings.ticker}-${index}`} className="earnings-card">
                                  <div className="card-header">
                                    <span className="ticker-symbol">{earnings.ticker || '-'}</span>
                                  </div>
                                  <div className="company-name">{earnings.company_name || '-'}</div>
                                  <div className="card-metrics">
                                    <div className="metric-row">
                                      <span className="metric-label">EPS:</span>
                                      <span className="metric-value" style={{ color: getEpsColor(earnings.eps_actual, earnings.eps_estimate) }}>
                                        {formatEps(earnings.eps_actual)}
                                      </span>
                                      <span className="metric-estimate">est. {formatEps(earnings.eps_estimate)}</span>
                                    </div>
                                    <div className="metric-row">
                                      <span className="metric-label">Revenue:</span>
                                      <span className="metric-value" style={{ color: getRevenueColor(earnings.revenue_actual, earnings.revenue_estimate) }}>
                                        {formatCurrency(earnings.revenue_actual)}
                                      </span>
                                      <span className="metric-estimate">est. {formatCurrency(earnings.revenue_estimate)}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="no-data">
              <p>No earnings data available for the selected criteria.</p>
              <p className="no-data-hint">Try adjusting your filters or selecting a different date.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EarningsComponent;